import { Injectable } from '@angular/core';
import { GalaxyDatePipe } from '@vendasta/galaxy/pipes';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { Observable } from 'rxjs';
import { PartnerService } from '../core/partner.service';
import { Campaign, RecipientCampaignStatusResponse } from '../marketing/campaign';
import { CampaignService } from '../marketing/campaign.service';
import {
  CampaignsService,
  MAILING_INFO_NOT_CONFIGURED,
} from '../marketing/marketing-automation-page/campaigns.service';

export interface CampaignDialogItem {
  campaignId: string;
  recipientCampaignId: string;
  name: string;
  description: string;
  currentStatus: string;
  statusText: string;
  statusColor: string;
  buttonText: string;
}

@Injectable({ providedIn: 'root' })
export class CampaignDialogItemStoreService {
  constructor(
    private campaignsService: CampaignsService,
    private legacyMAService: CampaignService,
    private snackbarService: SnackbarService,
    private partnerService: PartnerService,
  ) {}

  convertToCampaignDialogItem(campaign: Campaign): CampaignDialogItem {
    return {
      campaignId: campaign.id,
      recipientCampaignId: campaign.recipientCampaignId,
      name: campaign.name,
      description: this.getDescription(campaign),
      currentStatus: campaign.currentStatus,
      statusText: this.getStatusText(campaign.currentStatus),
      statusColor: this.getStatusColor(campaign.currentStatus),
      buttonText: this.getButtonText(campaign.currentStatus),
    };
  }

  private getStatusText(currentStatus: string): string {
    switch (currentStatus) {
      case 'active':
        return 'ACTIVE';
      case 'paused':
        return 'PAUSED';
      case 'buffering':
        return 'BUFFERING';
      case 'completed':
        return 'COMPLETED';
      default:
        console.error('unexpected status:', currentStatus);
        break;
    }
  }

  private getStatusColor(currentStatus: string): string {
    switch (currentStatus) {
      case 'active':
        return 'green';
      case 'paused':
        return 'gray';
      case 'buffering':
        return 'orange';
      case 'completed':
        return 'blue';
      default:
        break;
    }
  }

  private getDescription(campaign: Campaign): string {
    if (campaign.currentStatus === 'active' && campaign.nextStepScheduledDate) {
      const nextStepDate = new GalaxyDatePipe().transform(campaign.nextStepScheduledDate);
      const nextStepTime = new GalaxyDatePipe().transform(campaign.nextStepScheduledDate, DateFormat.shortTime);
      return `Next event will occur on ${nextStepDate} @ ${nextStepTime}`;
    }
    return '';
  }

  private getButtonText(currentStatus: string): string {
    if (currentStatus === null) {
      return 'Start';
    } else if (currentStatus === 'active') {
      return 'Pause';
    } else if (currentStatus === 'paused') {
      return 'Resume';
    }
  }

  updateStatus(
    campaign: Campaign,
    userId: string,
    accountGroupId: string,
  ): Observable<RecipientCampaignStatusResponse> {
    return this.legacyMAService.toggleUserOnCampaign(
      campaign.id,
      campaign.recipientCampaignId,
      campaign.currentStatus,
      userId,
      accountGroupId,
    );
  }

  handleUpdateStatusError(error: any, marketId: string): void {
    if (error.code === 500) {
      this.snackbarService.errorSnack('Could not change campaign state for the user. Please try again later.');
    } else if (error.code === 422) {
      this.snackbarService.errorSnack('Cannot send campaign: ' + error.data.message);
    } else if (error.code === 400 && error.data.message === MAILING_INFO_NOT_CONFIGURED) {
      const duration = 8000;
      const canCustomizeWhiteLabel = this.partnerService.CanCustomizeWhitelabel;
      if (canCustomizeWhiteLabel) {
        const snackBarRef = this.snackbarService.errorSnack(
          'Cannot start campaigns until a mailing address is provided.',
          'Edit Address',
          duration,
        );
        snackBarRef.onAction().subscribe(() => {
          window.location.href = '/customize-design/markets/' + marketId;
        });
      } else {
        this.snackbarService.errorSnack('Cannot start campaigns until a mailing address is provided.', null, duration);
      }
    }
  }
}
